/* eslint-disable unicorn/prevent-abbreviations */
import { Ikon } from '@studio/design-system/button';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { ItemHive } from './item-hive.jsx';
import { Logo } from '../../logo.jsx';
import type { GroupProps } from '../../types.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import type { RoomRecord } from '@studio/models/room';

/** Hive/PTZOptics vendor `<Group>` component with original UI. */
export const GroupHive = ({
  className,
  handler,
  organization,
  rooms,
  selection,
  ...props
}: GroupProps): ReactElement => {
  const passthru = 'pl-48';

  const { enabled: isOrganizationSettingsEnabled, loading } = useFeatureFlag(
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE
  );

  const renderOrgIcon = !loading && isOrganizationSettingsEnabled;

  const selectedOrgID = selection?.organizationId;
  const isOrgSelected = selectedOrgID === organization.id;

  const isOnlyOrgSelection = !!handler.onSelectOrganization && !handler.onSelectRoom;

  return (
    <div
      className={twMerge(
        !isOnlyOrgSelection && 'mb-6 border-b border-secondary/10 pb-6',
        className
      )}
      {...props}
    >
      {/* Account Heading */}
      {!!handler.onSelectRoom && (
        <div className='group mb-6 flex h-34 items-center gap-8 px-16' {...props}>
          <Logo name={organization.name} photo={organization.photoUrl} />
          <span className='font-demibold text-foreground'>{organization.name}</span>
          {!!handler.onShowOrganizationSettings && renderOrgIcon && (
            <Ikon
              className='opacity-0 transition hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100'
              icon='settings'
              onClick={(): void => {
                handler.onShowOrganizationSettings?.(organization.id);
              }}
            />
          )}
        </div>
      )}

      {/* Rooms */}
      {!!handler.onSelectRoom &&
        (rooms as RoomRecord[]).map((room) => {
          const isSelected = room.id === selection?.roomId;

          return (
            <ItemHive
              className={twMerge(
                isSelected
                  ? 'cursor-default border-l-6 border-l-primary bg-base-200 pl-[42px] text-foreground !opacity-100'
                  : passthru,
                'group flex items-center justify-items-center align-middle'
              )}
              disabled={isSelected}
              key={room.id}
              label={room.name}
              onSelect={(): void => {
                handler.onSelectRoom?.(room.id);
                handler.onSelectOrganization?.(room.organizationId);
              }}
            >
              {!!handler.onShowRoomSettings && (
                <Ikon
                  className='opacity-0 transition hover:!text-foreground hover:opacity-100 focus-visible:opacity-100 group-hover:text-muted group-hover:opacity-100'
                  icon='settings'
                  onClick={(e: React.MouseEvent): void => {
                    handler.onShowRoomSettings?.(room.id);
                    e.stopPropagation();
                  }}
                />
              )}
            </ItemHive>
          );
        })}

      {/* Org Selection */}
      {!!isOnlyOrgSelection && (
        <ItemHive
          className={twMerge(
            isOrgSelected ? 'cursor-default bg-base-200 text-foreground !opacity-100' : passthru,
            'group flex items-center justify-items-center py-[20px] pl-16 align-middle'
          )}
          disabled={isOrgSelected}
          icon={<Logo name={organization.name} photo={organization.photoUrl} />}
          key={organization.id}
          label={organization.name}
          onSelect={(): void => {
            handler.onSelectOrganization?.(organization.id);
          }}
        />
      )}

      {/* Add New Studio */}
      {!!handler.onCreateRoom && !!organization.permissions.canCreateRoom && (
        <ItemHive
          className={passthru}
          icon='plus'
          label='Add New Studio'
          onSelect={(): void => {
            handler.onCreateRoom?.(organization.id);
          }}
        />
      )}
    </div>
  );
};
